<template>
  <Page>
    <div class="space-y-6">
      <!-- Page Header -->
      <section id="project-detail-header" class="bg-background-card rounded-lg shadow-card p-6">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div class="flex items-start">
            <button
              @click="router.back()"
              class="mr-4 mt-1 rounded-full p-2 text-secondary transition-colors hover:bg-primary/10 hover:text-primary"
              :aria-label="'Back to projects'"
            >
              <i class="fa-solid fa-arrow-left"></i>
            </button>
            <div class="flex items-center">
              <div class="mr-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
                <i :class="project?.icon || 'fa-solid fa-folder'" class="text-primary"></i>
              </div>
              <div>
                <h1 class="text-2xl font-bold text-text">{{ project?.name || 'Loading...' }}</h1>
                <p class="text-text-light">{{ project?.description || 'No description available' }}</p>
              </div>
            </div>
          </div>
          <div class="flex items-center space-x-2">
          <Tag :value="project?.status || 'Unknown'" :severity="getStatusSeverity(project?.status)"
            class="font-medium" />
          </div>
        </div>
      </section>

    <!-- Loading State -->
    <section v-if="isLoading" class="bg-background-card rounded-lg shadow-card p-6">
      <div class="animate-pulse">
        <div class="h-4 bg-border rounded w-1/3 mb-2"></div>
        <div class="h-3 bg-border rounded w-1/2"></div>
      </div>
    </section>

    <!-- Error State -->
    <section v-else-if="error" class="bg-background-card rounded-lg shadow-card p-6">
      <div class="text-center py-8">
        <i class="fa-solid fa-exclamation-triangle text-4xl text-warning mb-4"></i>
        <h3 class="text-lg font-medium text-text mb-2">Failed to load project</h3>
        <p class="text-text-light mb-4">{{ error }}</p>
        <Button label="Retry" icon="pi pi-refresh" @click="fetchProject" class="bg-primary hover:bg-primary/90" />
      </div>
    </section>

    <!-- Project Content -->
    <section v-else-if="project" class="space-y-6">
      <!-- Project Information Card -->
      <div class="bg-background-card rounded-lg shadow-card p-6">
        <div class="flex items-center justify-between mb-4">
          <h2 class="text-lg font-semibold text-text">Project Information</h2>
          <div class="flex items-center space-x-2">
            <Button v-if="!projectEditing.editingState.value.isEditing" label="Edit" icon="pi pi-pencil" size="small"
              outlined @click="projectEditing.startEditing(project)" class="text-sm" />
            <div v-else class="flex items-center space-x-2">
              <Button label="Save" icon="pi pi-check" size="small" @click="handleSaveProject"
                :loading="projectEditing.loading.value" :disabled="!projectEditing.isValid.value" class="text-sm" />
              <Button label="Cancel" icon="pi pi-times" size="small" outlined @click="projectEditing.cancelEditing"
                :disabled="projectEditing.loading.value" class="text-sm" />
            </div>
          </div>
        </div>

        <!-- Project Basic Info -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <!-- Project Name -->
          <div>
            <dt class="text-sm font-medium text-text-light mb-1">Project Name</dt>
            <dd v-if="!projectEditing.editingState.value.isEditing" class="text-text font-medium">
              {{ project.name || 'Untitled Project' }}
            </dd>
            <InputText v-else :model-value="projectEditing.editingState.value.editableFields.name"
              @update:model-value="(value: string | undefined) => { if (value !== undefined) projectEditing.updateField('name', value); }"
              placeholder="Enter project name..." class="w-full" />
          </div>

          <!-- Project Status -->
          <div>
            <dt class="text-sm font-medium text-text-light mb-1">Status</dt>
            <dd v-if="!projectEditing.editingState.value.isEditing">
              <Tag :value="project.status" :severity="getStatusSeverity(project?.status)" class="font-medium" />
            </dd>
            <Select v-else :model-value="projectEditing.editingState.value.editableFields.status"
              @update:model-value="(value: string | undefined) => { if (value !== undefined) projectEditing.updateField('status', value); }" :options="[
                { label: 'Active', value: 'Active' },
                { label: 'Archived', value: 'Archived' },
                { label: 'Template', value: 'Template' }
              ]" option-label="label" option-value="value" placeholder="Select status" class="w-full" />
          </div>
        </div>

        <!-- Project Description -->
        <div class="mb-6">
          <dt class="text-sm font-medium text-text-light mb-1">Description</dt>
          <dd v-if="!projectEditing.editingState.value.isEditing" class="text-text">
            {{ project.description || 'No description available' }}
          </dd>
          <Textarea v-else :model-value="projectEditing.editingState.value.editableFields.description"
            @update:model-value="(value: string | undefined) => { if (value !== undefined) projectEditing.updateField('description', value); }"
            placeholder="Enter project description..." rows="3" class="w-full rounded-lg" />
        </div>

        <!-- Metadata -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <dt class="text-sm font-medium text-text-light mb-1">Created</dt>
            <dd class="text-text">{{ formatCreatorModifier(project.createdBy, project.createdAt, 'Created') }}</dd>
          </div>
          <div>
            <dt class="text-sm font-medium text-text-light mb-1">Last Modified</dt>
            <dd class="text-text">{{ formatCreatorModifier(project.lastModifiedBy, project.lastModifiedAt, 'Last Modified') }}</dd>
          </div>
        </div>

        <!-- Error display for editing -->
        <div v-if="projectEditing.error.value"
          class="mt-4 p-3 bg-error/10 border border-error/20 text-error rounded-lg">
          <div class="flex items-center">
            <i class="fa-solid fa-exclamation-triangle mr-2"></i>
            <span class="text-sm">{{ projectEditing.error.value }}</span>
          </div>
        </div>
      </div>

      <!-- Team Management Card -->
      <div class="bg-background-card rounded-lg shadow-card overflow-hidden">
        <div class="border-b border-border p-6">
          <div class="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div class="space-y-4">
              <div>
                <div class="flex flex-wrap items-center gap-3">
                  <h2 class="text-lg font-semibold text-text">Team Members</h2>
                  <div
                    v-if="teamMembers.length > 0"
                    class="rounded-full bg-primary/10 px-2 py-1 text-sm font-medium text-primary"
                  >
                    {{ teamMembers.length }} total
                  </div>
                  <div class="rounded-full bg-background px-2 py-1 text-sm font-medium text-text-light border border-border">
                    {{ projectOwnerCount }} owner{{ projectOwnerCount === 1 ? '' : 's' }}
                  </div>
                  <div
                    v-if="bulkTeamActions.hasSelection.value"
                    class="rounded-full bg-warning/10 px-2 py-1 text-sm font-medium text-warning"
                  >
                    {{ bulkTeamActions.selectedCount.value }} selected
                  </div>
                </div>
                <p class="mt-2 max-w-2xl text-sm text-text-light">
                  Manage project access from one place. Add teammates in a focused modal, adjust roles inline, and keep at least one Project Owner assigned.
                </p>
              </div>

              <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div
                  v-for="roleSummary in roleSummaryCards"
                  :key="roleSummary.role"
                  class="rounded-xl border border-border bg-background p-4"
                >
                  <div class="flex items-start justify-between gap-3">
                    <div class="text-xs font-semibold uppercase tracking-wide text-text-light">{{ roleSummary.label }}</div>
                    <div class="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <i :class="roleSummary.icon"></i>
                    </div>
                  </div>
                  <div class="mt-3 text-2xl font-semibold text-text">{{ roleSummary.count }}</div>
                  <div class="mt-1 text-sm text-text-light">
                    {{ roleSummary.count }} member{{ roleSummary.count === 1 ? '' : 's' }} assigned
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-2 xl:justify-end">
              <Button
                label="Add Members"
                icon="pi pi-plus"
                size="small"
                @click="openAddMembersModal"
                :disabled="teamManagement.loading.value || isAddingMembers"
                class="text-sm"
              />
              <Button
                label="Copy Members"
                icon="pi pi-copy"
                size="small"
                severity="secondary"
                @click="openCopyMembersModal"
                :disabled="teamLoading || isCopyingMembers"
                class="text-sm"
              />
              <Button
                label="Refresh"
                icon="pi pi-refresh"
                size="small"
                severity="secondary"
                @click="refreshTeamMembers"
                :loading="teamLoading"
                class="text-sm"
              />
            </div>
          </div>

          <div
            v-if="bulkTeamActions.hasSelection.value"
            class="mt-6 rounded-xl border border-warning/20 bg-warning/5 p-4"
          >
            <div class="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p class="font-medium text-text">
                  {{ bulkTeamActions.selectedCount.value }} member{{ bulkTeamActions.selectedCount.value === 1 ? '' : 's' }} selected
                </p>
                <p class="text-sm text-text-light">
                  Apply role changes or remove the selected members while respecting the owner safeguard.
                </p>
              </div>
              <div class="flex flex-wrap items-center gap-2">
                <div class="relative">
                  <Select
                    v-model="bulkRoleSelection"
                    :options="teamManagement.roleOptions.value"
                    option-label="label"
                    option-value="value"
                    placeholder="Change role..."
                    size="small"
                    :disabled="!canBulkChangeRole"
                    class="text-sm transition-colors"
                    :class="{ 'opacity-50 cursor-not-allowed': !canBulkChangeRole }"
                    :title="!canBulkChangeRole ? 'Cannot change roles as it would leave the project without any Project Owner' : ''"
                    @change="handleBulkChangeRole($event.value)"
                  />
                  <div
                    v-if="!canBulkChangeRole"
                    class="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-warning"
                  >
                    <i class="fa-solid fa-lock text-xs text-text-inverse" style="font-size: 8px;"></i>
                  </div>
                </div>
                <div class="relative">
                  <Button
                    label="Remove Selected"
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    @click="handleBulkRemoveMembers"
                    :disabled="bulkTeamActions.loading.value || !canBulkRemove"
                    :class="{ 'opacity-50 cursor-not-allowed': !canBulkRemove }"
                    :title="!canBulkRemove ? 'Cannot remove selected members as it would leave the project without any Project Owner' : ''"
                    class="text-sm"
                  />
                  <div
                    v-if="!canBulkRemove"
                    class="absolute -top-1 -right-1 flex h-3 w-3 items-center justify-center rounded-full bg-warning"
                  >
                    <i class="fa-solid fa-lock text-xs text-text-inverse" style="font-size: 8px;"></i>
                  </div>
                </div>
                <Button
                  label="Clear Selection"
                  icon="pi pi-times"
                  size="small"
                  text
                  @click="bulkTeamActions.clearSelection"
                  class="text-sm"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="p-6">
          <!-- Loading state -->
          <div v-if="teamLoading" class="space-y-3">
            <div v-for="i in 3" :key="i"
              class="flex items-center justify-between p-4 bg-background rounded-lg border border-border animate-pulse">
              <div class="flex items-center">
                <div class="w-10 h-10 rounded-full bg-border mr-3"></div>
                <div>
                  <div class="h-4 bg-border rounded w-32 mb-2"></div>
                  <div class="h-3 bg-border rounded w-24"></div>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <div class="w-32 h-8 bg-border rounded"></div>
                <div class="w-8 h-8 bg-border rounded"></div>
              </div>
            </div>
          </div>

          <div v-else-if="teamMembers.length > 0" class="space-y-3">
            <!-- Info Message for Disabled Actions -->
            <Message v-if="hasDisabledActions" severity="info" :closable="false" icon="pi pi-info-circle">
              <div class="flex items-start space-x-2">
                <i class="fa-solid fa-lock text-info mt-0.5"></i>
                <div>
                  <div class="font-medium text-info mb-1">Action Restrictions</div>
                  <div class="text-sm text-info/80">{{ disabledActionsReason }}</div>
                </div>
              </div>
            </Message>

            <!-- Select All Checkbox -->
            <div class="flex flex-col gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex items-center">
                <Checkbox :model-value="bulkTeamActions.selection.value.allSelected"
                  @update:model-value="bulkTeamActions.selection.value.allSelected ? bulkTeamActions.clearSelection() : bulkTeamActions.selectAllMembers(teamMembers)"
                  binary class="mr-3" />
                <div>
                  <div class="text-sm font-medium text-primary">
                    {{ bulkTeamActions.selection.value.allSelected ? 'Deselect all members' : 'Select all members' }}
                  </div>
                  <div class="text-xs text-text-light">Bulk actions become available once team members are selected.</div>
                </div>
              </div>
              <div class="text-xs text-text-light">{{ teamMembers.length }} visible</div>
            </div>

            <!-- Individual Team Members -->
            <div v-for="member in teamMembers" :key="member.userId"
              class="flex flex-col gap-4 rounded-xl border border-border bg-background p-4 transition-all hover:shadow-xs lg:flex-row lg:items-center lg:justify-between"
              :class="{ 'border-primary bg-primary/5': bulkTeamActions.isMemberSelected(member.userId || '') }">
              <div class="flex items-center">
                <!-- Selection Checkbox -->
                <Checkbox :model-value="bulkTeamActions.isMemberSelected(member.userId || '')"
                  @update:model-value="bulkTeamActions.toggleMemberSelection(member.userId || '')" binary
                  class="mr-3" />

                <Avatar :label="$getInitials(member.fullName || 'U')" shape="circle"
                  :class="$getGradientClass($getInitials(member.fullName || 'U')) + ' w-10 h-10 text-sm font-bold text-white mr-4'" />
                <div>
                  <div class="font-medium text-text">{{ member.fullName || 'Unknown User' }}</div>
                  <div class="text-sm text-text-light">{{ member.email }}</div>
                </div>
              </div>

              <!-- Team management controls -->
              <div class="flex items-center gap-3">
                <!-- Role selector -->
                <div class="relative">
                  <Select :model-value="getRoleName(member.role!)"
                    @update:model-value="(newRole: ProjectRoleType) => updateMemberRole(member, newRole)"
                    :options="teamManagement.roleOptions.value" option-label="label" option-value="value"
                    placeholder="Select role" :disabled="teamManagement.isRoleDropdownDisabled(member, teamMembers)"
                    class="w-60 text-sm transition-colors" :class="{
                       'opacity-50 cursor-not-allowed': teamManagement.isRoleDropdownDisabled(member, teamMembers),
                       'hover:bg-accent-dark': !teamManagement.isRoleDropdownDisabled(member, teamMembers)
                    }"
                    :title="teamManagement.isRoleDropdownDisabled(member, teamMembers) ? 'Cannot change role of the only Project Owner' : ''"
                    :loading="teamManagement.loading.value && updatingMember === member.userId" />
                  <!-- Show a lock icon when disabled -->
                  <div v-if="teamManagement.isRoleDropdownDisabled(member, teamMembers)"
                    class="absolute -top-1 -right-1 w-4 h-4 bg-warning rounded-full flex items-center justify-center">
                    <i class="fa-solid fa-lock text-xs text-text-inverse"></i>
                  </div>
                </div>

                <!-- Remove button -->
                <div class="relative">
                  <Button icon="pi pi-trash" size="small" severity="danger" outlined
                    @click="removeMemberWithConfirm(member)"
                    :disabled="teamManagement.isDeleteButtonDisabled(member, teamMembers)"
                    :loading="teamManagement.loading.value && removingMember === member.userId" :class="{
                      'opacity-50 cursor-not-allowed': teamManagement.isDeleteButtonDisabled(member, teamMembers),
                      'hover:bg-error/20': !teamManagement.isDeleteButtonDisabled(member, teamMembers)
                    }"
                    :title="teamManagement.isDeleteButtonDisabled(member, teamMembers) ? 'Cannot remove the only Project Owner' : 'Remove member'"
                    v-tooltip.top="teamManagement.isDeleteButtonDisabled(member, teamMembers) ? 'Cannot remove the only Project Owner' : 'Remove member'" />
                  <!-- Show a lock icon when disabled -->
                  <div v-if="teamManagement.isDeleteButtonDisabled(member, teamMembers)"
                    class="absolute -top-1 -right-1 w-3 h-3 bg-warning rounded-full flex items-center justify-center">
                    <i class="fa-solid fa-lock text-xs text-text-inverse" style="font-size: 8px;"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div v-else class="text-center py-12">
            <div class="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <i class="fa-solid fa-users text-2xl text-primary"></i>
            </div>
            <h3 class="text-lg font-medium text-text mb-2">No team members yet</h3>
            <p class="text-text-light mb-4">Start building your team by adding members to this project.</p>
            <Button label="Add Members" icon="pi pi-plus" @click="openAddMembersModal"
              :disabled="teamLoading || isAddingMembers" />
          </div>

          <!-- Error state -->
          <div v-if="teamError" class="mt-4 p-4 bg-error/10 border border-error/20 text-error rounded-lg">
            <div class="flex items-center">
              <i class="fa-solid fa-exclamation-triangle mr-2"></i>
              <span class="font-medium">{{ teamError }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <AddMembersModal
      v-model="showAddMembersModal"
      :existing-members="teamMembers"
      :close-on-add="false"
      :submitting="isAddingMembers"
      :external-error="addMembersActionError"
      @add="handleAddMembers"
    />
    <CopyMembersModal
      v-model="showCopyMembersModal"
      :current-project-id="projectId"
      :existing-members="teamMembers"
      :close-on-copy="false"
      :submitting="isCopyingMembers"
      :external-error="copyMembersActionError"
      @copy="handleCopyMembers"
    />
    </div>
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useProject } from '@/features/project-management/composables/useProject';
import { useProjectTeamManagement } from '@/features/project-management/composables/useProjectTeamManagement';
import { useProjectEditing } from '@/features/project-management/composables/useProjectEditing';
import { useBulkTeamActions } from '@/features/project-management/composables/useBulkTeamActions';
import type { AddMembersSelection } from '@/features/project-management/composables/useAddMembers';
import {
  copyProjectMembersToTarget,
  type CopyProjectMembersSelection,
} from '@/features/project-management/composables/useCopyProjectMembers';
import { formatCreatorModifier } from '@/features/project-management/utils/metadataFormatters';
import { ProjectTeamService, type UiProjectTeamMember } from '@/shared/services/projectTeam';
import { ProjectRole, type ProjectRoleType, iconMap } from '@/core/types/models/RoleTypes';
import { pascalToHuman } from '@/shared/utils/stringUtils';
import AddMembersModal from '@/features/project-management/components/AddMembersModal.vue';
import CopyMembersModal from '@/features/project-management/components/CopyMembersModal.vue';
import Page from '@/shared/components/layouts/Page.vue';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import Message from 'primevue/message';
import { useConfirm } from 'primevue/useconfirm';
import { useToast } from 'primevue/usetoast';
import { addToast } from '@/shared/utils/toastMessages';

definePage({
  meta: {
    requiresProject: false,
  },
});

const route = useRoute();
const router = useRouter();
const confirm = useConfirm();
const toast = useToast();

const { project, isLoading, error, loadProject } = useProject();
const teamManagement = useProjectTeamManagement();
const projectEditing = useProjectEditing();
const bulkTeamActions = useBulkTeamActions();

// Team management state
const teamMembers = ref<UiProjectTeamMember[]>([]);
const teamLoading = ref(false);
const teamError = ref<string | null>(null);
const updatingMember = ref<string | null>(null);
const removingMember = ref<string | null>(null);

// Add members functionality
const showAddMembersModal = ref(false);
const isAddingMembers = ref(false);
const addMembersActionError = ref<string | null>(null);
const showCopyMembersModal = ref(false);
const isCopyingMembers = ref(false);
const copyMembersActionError = ref<string | null>(null);

// Bulk actions
const bulkRoleSelection = ref<ProjectRoleType | null>(null);

const projectId = computed((): string => {
  const { id } = route.params as { id?: string | string[] };
  if (typeof id === 'string') {
    return id;
  }

  if (Array.isArray(id)) {
    return id[0] ?? '';
  }

  return '';
});

// Bulk actions protection
const canBulkRemove = computed(() => {
  return !bulkTeamActions.wouldLeaveProjectWithoutOwner(teamMembers.value, 'remove');
});

const canBulkChangeRole = computed(() => {
  return bulkRoleSelection.value ? !bulkTeamActions.wouldLeaveProjectWithoutOwner(teamMembers.value, 'changeRole', bulkRoleSelection.value) : true;
});

const projectOwnerCount = computed(() => teamManagement.getProjectOwnersCount(teamMembers.value));

const roleSummaryCards = computed(() => {
  const roleCounts: Record<ProjectRoleType, number> = {
    ProjectOwner: 0,
    ProjectContributor: 0,
    ProjectValidator: 0,
    ProjectViewer: 0,
  };

  for (const member of teamMembers.value) {
    const role = getRoleName(member.role);
    if (role) {
      roleCounts[role] += 1;
    }
  }

  return (Object.keys(ProjectRole) as ProjectRoleType[]).map((role) => ({
    role,
    label: `${pascalToHuman(role).replace(/^Project\s+/, '')}s`,
    count: roleCounts[role],
    icon: iconMap[role],
  }));
});

// Check if any delete buttons or role dropdowns are disabled to show info message
const hasDisabledActions = computed(() => {
  return teamMembers.value.some(member => 
    teamManagement.isDeleteButtonDisabled(member, teamMembers.value) ||
    teamManagement.isRoleDropdownDisabled(member, teamMembers.value)
  );
});

// Get the reason why actions are disabled
const disabledActionsReason = computed(() => {
  if (!hasDisabledActions.value) return '';
  
  // Count project owners - check if role matches the ProjectOwner enum value
  const ownerCount = teamMembers.value.filter(member => 
    member.role === ProjectRole.ProjectOwner
  ).length;
  
  if (ownerCount === 1) {
    return 'Some actions are restricted for the project owner because every project must have at least one owner. To modify or remove this member, first assign the Project Owner role to another team member.';
  }
  
  return 'Some actions are disabled due to project requirements.';
});

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error && error.message
    ? error.message
    : fallback;
}

function openAddMembersModal(): void {
  addMembersActionError.value = null;
  showAddMembersModal.value = true;
}

function closeAddMembersModal(): void {
  showAddMembersModal.value = false;
  addMembersActionError.value = null;
}

function openCopyMembersModal(): void {
  copyMembersActionError.value = null;
  showCopyMembersModal.value = true;
}

function closeCopyMembersModal(): void {
  showCopyMembersModal.value = false;
  copyMembersActionError.value = null;
}

async function handleAddMembers(selection: AddMembersSelection) {
  if (!projectId.value) return;

  const userIds = selection.users
    .map((user) => user.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);

  if (userIds.length === 0) {
    addMembersActionError.value = 'No users selected';
    return;
  }

  isAddingMembers.value = true;
  addMembersActionError.value = null;

  try {
    const promises = userIds.map((userId) =>
      ProjectTeamService.addTeamMember(projectId.value, {
        projectId: projectId.value,
        userId,
        role: selection.role
      })
    );

    await Promise.all(promises);
    await fetchTeamMembers();
    closeAddMembersModal();

    addToast(toast, {
      severity: 'success',
      summary: 'Success',
      detail: `Added ${userIds.length} member(s) to the project team`,
    });

  } catch (error: unknown) {
    addMembersActionError.value = getErrorMessage(error, 'Failed to add team members');
    console.error('Error adding team members:', error);
  } finally {
    isAddingMembers.value = false;
  }
}

async function handleCopyMembers(selection: CopyProjectMembersSelection): Promise<void> {
  if (!projectId.value) {
    return;
  }

  isCopyingMembers.value = true;
  copyMembersActionError.value = null;

  try {
    const copiedMembers = await copyProjectMembersToTarget(
      projectId.value,
      selection.members,
      teamMembers.value,
    );

    await fetchTeamMembers();
    closeCopyMembersModal();

    addToast(toast, {
      severity: 'success',
      summary: 'Success',
      detail: copiedMembers.length === 1
        ? `Copied 1 member from ${selection.sourceProjectName}`
        : `Copied ${copiedMembers.length} members from ${selection.sourceProjectName}`,
    });
  } catch (error: unknown) {
    copyMembersActionError.value = getErrorMessage(error, 'Failed to copy project members');
    console.error('Error copying project members:', error);
  } finally {
    isCopyingMembers.value = false;
  }
}

async function fetchProject() {
  if (!projectId.value) {
    // loadProject will validate and set error
    await loadProject('');
    return;
  }

  await loadProject(projectId.value);
}

async function fetchTeamMembers() {
  if (!projectId.value) return;

  try {
    teamLoading.value = true;
    teamError.value = null;
    teamMembers.value = await ProjectTeamService.getTeamMembers(projectId.value);
  } catch (error: unknown) {
    teamError.value = getErrorMessage(error, 'Failed to fetch team members');
    console.error('Error fetching team members:', error);
  } finally {
    teamLoading.value = false;
  }
}

async function refreshTeamMembers() {
  await fetchTeamMembers();
}

function getRoleName(role: string | null | undefined): ProjectRoleType | undefined {
  if (!role) return undefined;

  // Handle string-based roles (current API format)
  if (typeof role === 'string' && role in ProjectRole) {
    return role as ProjectRoleType;
  }

  return undefined;
}

async function updateMemberRole(member: UiProjectTeamMember, newRole: ProjectRoleType) {
  if (!member.userId || !projectId.value) return;

  updatingMember.value = member.userId;

  const result = await teamManagement.updateMemberRole(projectId.value, member.userId, newRole, teamMembers.value);

  if (result) {
    // Update the local team members list
    const index = teamMembers.value.findIndex(m => m.userId === member.userId);
    if (index !== -1) {
      teamMembers.value[index] = result;
    }

    addToast(toast, {
      severity: 'success',
      summary: 'Success',
      detail: `Updated ${member.fullName || 'member'}'s role`,
    });
  } else if (teamManagement.error.value) {
    addToast(toast, {
      severity: 'error',
      summary: 'Error',
      detail: teamManagement.error.value,
    });
  }

  updatingMember.value = null;
}

function removeMemberWithConfirm(member: UiProjectTeamMember) {
  confirm.require({
    message: `Are you sure you want to remove ${member.fullName || 'this member'} from the project team?`,
    header: 'Remove Team Member',
    icon: 'pi pi-exclamation-triangle',
    rejectProps: {
      label: 'Cancel',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Remove',
      severity: 'danger'
    },
    accept: () => removeMember(member)
  });
}

async function removeMember(member: UiProjectTeamMember) {
  if (!member.userId || !projectId.value) return;

  removingMember.value = member.userId;

  const success = await teamManagement.removeMember(projectId.value, member.userId, teamMembers.value);

  if (success) {
    // Remove from local team members list
    teamMembers.value = teamMembers.value.filter(m => m.userId !== member.userId);

    addToast(toast, {
      severity: 'success',
      summary: 'Success',
      detail: `Removed ${member.fullName || 'member'} from the project team`,
    });
  } else if (teamManagement.error.value) {
    addToast(toast, {
      severity: 'error',
      summary: 'Error',
      detail: teamManagement.error.value,
    });
  }

  removingMember.value = null;
}

function getStatusSeverity(status?: string) {
  switch (status) {
    case 'Active': return 'success';
    case 'Archived': return 'secondary';
    case 'Pending': return 'warning';
    case 'Template': return 'info';
    default: return 'secondary';
  }
}

// Project editing functions
async function handleSaveProject() {
  if (!project.value?.id) return;

  const updatedProject = await projectEditing.saveChanges(project.value.id);
  if (updatedProject) {
    project.value = updatedProject;
    addToast(toast, {
      severity: 'success',
      summary: 'Success',
      detail: 'Project updated successfully',
    });
  } else if (projectEditing.error.value) {
    addToast(toast, {
      severity: 'error',
      summary: 'Error',
      detail: projectEditing.error.value,
    });
  }
}

// Bulk team actions functions
async function handleBulkRemoveMembers() {
  if (!project.value?.id || !bulkTeamActions.hasSelection.value) return;

  confirm.require({
    message: `Are you sure you want to remove ${bulkTeamActions.selectedCount.value} selected member(s) from the project team?`,
    header: 'Remove Team Members',
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: async () => {
      const success = await bulkTeamActions.bulkRemoveMembers(project.value!.id!, teamMembers.value);
      if (success) {
        addToast(toast, {
          severity: 'success',
          summary: 'Success',
          detail: `Removed ${bulkTeamActions.selectedCount.value} member(s) from the project team`,
        });
        await fetchTeamMembers(); // Refresh team members list
      } else if (bulkTeamActions.error.value) {
        addToast(toast, {
          severity: 'error',
          summary: 'Error',
          detail: bulkTeamActions.error.value,
        });
      }
    }
  });
}

async function handleBulkChangeRole(newRole: ProjectRoleType) {
  if (!project.value?.id || !bulkTeamActions.hasSelection.value) return;

  const updatedMembers = await bulkTeamActions.bulkChangeRole(project.value.id, teamMembers.value, newRole);
  if (updatedMembers.length > 0) {
    // Update local team members list
    updatedMembers.forEach(updatedMember => {
      const index = teamMembers.value.findIndex(m => m.userId === updatedMember.userId);
      if (index !== -1) {
        teamMembers.value[index] = updatedMember;
      }
    });

    addToast(toast, {
      severity: 'success',
      summary: 'Success',
      detail: `Updated role for ${updatedMembers.length} member(s)`,
    });
  } else if (bulkTeamActions.error.value) {
    addToast(toast, {
      severity: 'error',
      summary: 'Error',
      detail: bulkTeamActions.error.value,
    });
  }
}

onMounted(async () => {
  await fetchProject();
  await fetchTeamMembers();
});
</script>
